import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useDeviationAttributes } from '../../../DeviationViews/hooks/useDeviationAttributes';
import { isTextualDeviation } from '../../../DeviationViews/_helpers';
import AdoptableSeller from '../../buttons/AdoptableSeller';
import AdoptableBuyer from '../../buttons/AdoptableBuyer';
import ActionBarStrip from '../../_partials/ActionBarStrip';
import CentralColumn from '../../../DeviationPage/_partials/CentralColumn';
import DeviationFlags from '../../buttons/DeviationFlags';
import { PremiumDownloadButton } from '../../buttons/DownloadButton';
import SellYourArtLink from '../../buttons/SellYourArtLink';
import { StashElementPlacementContext } from '../../ElementPlacementContext/variants/stash/context';
import { useElementPlacementForArea } from '../../ElementPlacementContext';
import { StashDeviationElementPlacement } from '../../ElementPlacementContext/variants/stash/types';
import { ActionBarProps as Props } from '../../types';
import EditButton from './EditButton';
import CopyLinkButton from './CopyLinkButton';
import MoreButton from './MoreButton';
import FreeDownloadButton from './DownloadButton';

import s from './StashActionBar.scss';

const StashActionBar: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  isOwner,
  className,
}) => {
  const placementMap = useElementPlacementForArea(
    StashDeviationElementPlacement.PrimaryBar,
    StashElementPlacementContext
  );
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { hasFlag } = useDeviationAttributes(deviation, isOwner);

  const isTextual = isTextualDeviation(deviation);

  return (
    <ActionBarStrip className={cn(s['root'], className)}>
      <CentralColumn>
        {isMobile ? (
          <div className={s['mobile-content-root']}>
            <div className={s['mobile-visibility-and-more-actions-row']}>
              {isOwner && (
                <span className={s['visibility-disclosure']}>
                  {t('stashBanner.message', {
                    postProcess: ['reactPostprocessor', 'featureBrancher'],
                    prefix: (
                      <span className={s['prefix-message']}>{t('stashBanner.message.prefix')}</span>
                    ),
                  })}
                </span>
              )}

              {placementMap.meta.hasPrimaryMoreActionsMenu && (
                <div className={s['mobile-more-button-container']}>
                  <MoreButton deviation={deviation} className={s['mobile-more-button']} />
                </div>
              )}
            </div>
            <div className={s['mobile-copy-download-edit-row']}>
              {placementMap.sellDeviation && <SellYourArtLink deviation={deviation} />}
              {placementMap.copyLink && <CopyLinkButton deviation={deviation} />}
              {placementMap.freeDownload && <FreeDownloadButton deviation={deviation} />}
              {placementMap.editDeviation && <EditButton deviation={deviation} />}
            </div>
          </div>
        ) : (
          <div className={s['desktop-content-root']}>
            <div className={s['left-side']}>
              {hasFlag && <DeviationFlags deviation={deviation} disableLabel={isTextual} />}
              {placementMap.elementGroups.adoptableSeller.groupPlacement && (
                <AdoptableSeller
                  deviation={deviation}
                  manualElementGroup={placementMap.elementGroups.adoptableSeller}
                />
              )}
            </div>
            <div className={s['right-side']}>
              {placementMap.elementGroups.adoptableBuyer.groupPlacement && (
                <AdoptableBuyer
                  deviation={deviation}
                  manualElementGroup={placementMap.elementGroups.adoptableBuyer}
                />
              )}
              {placementMap.sellDeviation && <SellYourArtLink deviation={deviation} />}
              {placementMap.copyLink && <CopyLinkButton deviation={deviation} />}
              {placementMap.editDeviation && <EditButton deviation={deviation} />}
              {placementMap.freeDownload && <FreeDownloadButton deviation={deviation} />}
              {placementMap.premiumDownload && <PremiumDownloadButton deviation={deviation} />}
              {placementMap.meta.hasPrimaryMoreActionsMenu && <MoreButton deviation={deviation} />}
            </div>
          </div>
        )}
      </CentralColumn>
    </ActionBarStrip>
  );
};
StashActionBar.displayName = 'StashActionBar';

export default StashActionBar;
